/**
 * @engine/vfx — VFX preset builders (effects group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts to
 * keep that module focused on content-type / importer / serialization.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Black Hole - Multi-system effect with accretion disk, relativistic jets, and Hawking radiation.
 *
 * Systems:
 * 1. Accretion Disk - Particles spiral inward with vortex motion, HDR orange/white colors
 * 2. Jet Up - Relativistic jet shooting upward, purple/blue HDR
 * 3. Jet Down - Relativistic jet shooting downward
 * 4. Event Horizon - Dark core particles, very dim
 * 5. Hawking Radiation - Rare bright escaping particles
 */

export function createTornadoVFX(name: string = "Tornado"): VFXGraphData {
  const template: VFXGraphData = {
    id: "tornado_effect",
    name: "Tornado",
    nodes: [
      // Spawn
      {
        id: "spawn",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 200 },
        contextId: "ctx_spawn",
      },
      // Initialize
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 3, max: 5 },
        contextId: "ctx_init",
      },
      {
        id: "init_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 60,
          size: { x: 120, y: 5, z: 120 },
          arc: 360,
          center: { x: 0, y: -100, z: 0 },
        },
        contextId: "ctx_init",
      },
      {
        id: "init_velocity",
        type: "vfx.init.velocity_cone",
        position: { x: 50, y: 250 },
        inputValues: {
          direction: { x: 0, y: 1, z: 0 },
          angle: 25,
          speed: 40,
          speedVariance: 15,
        },
        contextId: "ctx_init",
      },
      {
        id: "init_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 2, max: 5 },
        contextId: "ctx_init",
      },
      {
        id: "init_scale",
        type: "vfx.init.scale",
        position: { x: 50, y: 400 },
        inputValues: { scaleX: 0.3, scaleY: 2.0 },
        contextId: "ctx_init",
      },
      {
        id: "init_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 0.6, g: 0.5, b: 0.4, a: 1 } },
        contextId: "ctx_init",
      },
      // Update - 2D tornado simulation (side view)
      {
        id: "update_attract",
        type: "vfx.update.attract",
        position: { x: 50, y: 50 },
        inputValues: {
          target: { x: 0, y: 100, z: 0 },
          strength: 15,
          maxDistance: 300,
          falloff: 0,
        },
        contextId: "ctx_update",
      },
      {
        id: "update_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 150 },
        inputValues: { intensity: 35, scale: 0.015, speed: 1.5 },
        contextId: "ctx_update",
      },
      {
        id: "update_limit",
        type: "vfx.update.limit_velocity",
        position: { x: 50, y: 250 },
        inputValues: { minSpeed: 5, maxSpeed: 60, damping: 0.3 },
        contextId: "ctx_update",
      },
      {
        id: "update_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.7, g: 0.55, b: 0.4, a: 0.9 } },
              { t: 0.6, color: { r: 0.5, g: 0.4, b: 0.35, a: 0.7 } },
              { t: 0.9, color: { r: 0.35, g: 0.3, b: 0.25, a: 0.3 } },
              { t: 1, color: { r: 0.3, g: 0.25, b: 0.2, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update",
      },
      {
        id: "update_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 450 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.2, v: 0.8 },
              { t: 0.5, v: 1.0 },
              { t: 0.8, v: 0.6 },
              { t: 1, v: 0 },
            ],
          },
          scale: 8,
        },
        contextId: "ctx_update",
      },
      // Output
      {
        id: "output",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, softParticle: true },
        contextId: "ctx_output",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_lifetime",
          "init_position",
          "init_velocity",
          "init_size",
          "init_scale",
          "init_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 450 },
        nodeIds: [
          "update_attract",
          "update_curl",
          "update_limit",
          "update_color",
          "update_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1300 },
        size: { width: 280, height: 120 },
        nodeIds: ["output"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow1",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow2",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow3",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: { ctx_init: { capacity: 1000, boundsMode: "auto" } },
    capacity: { maxParticles: 1000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Force Field - Particles attracted to a sphere surface.
 * Demonstrates: conform_to_sphere, limit_velocity, noise_offset
 */
